// Starter Tape audio engine — turns a song (see starterTape.js) into sound
// with Tone.js. Kept thin on purpose: all musical decisions live in the pure
// song model; this file only builds the instruments and schedules notes.
//
// Routing: every instrument feeds its own channel strip — a filter (its
// brightness), then a dry level to the master plus two sends, one into a
// shared reverb room and one into a shared echo that repeats on a dotted
// eighth. Each track's `soundOf()` settings set those levels.
//
// Tone.js is loaded lazily on the first user gesture (browsers only allow
// audio to start from a click or key press), so it stays out of the bundle
// for the rest of the site until someone opens the studio and presses play.

import {
  HAT,
  KICK,
  SNARE,
  brightnessHz,
  loopSeconds,
  midiToFreq,
  playbackEvents,
  soundOf,
  totalSteps,
} from "./starterTape";

let enginePromise = null;

// The shared engine, created on first call. Call it from a click handler.
export const getEngine = () => {
  if (!enginePromise) {
    enginePromise = createEngine().catch((err) => {
      enginePromise = null; // let a later click retry
      throw err;
    });
  }
  return enginePromise;
};

// How far each instrument's filter opens for a given brightness: pads sit
// where the brightness curve puts them, bells ring higher. Drums have no
// brightness control, so their filter stays wide open.
const FILTER_SCALE = { pad: 1, bell: 4, bass: 0.5 };

const createEngine = async () => {
  const Tone = await import("tone");
  await Tone.start();

  const transport = Tone.getTransport();
  const limiter = new Tone.Limiter(-2).toDestination();
  const master = new Tone.Volume(-4).connect(limiter);

  // One shared room (fully wet; each channel decides how much to send) and
  // one shared echo that also spills a little into the room.
  const room = new Tone.Reverb({ decay: 7, preDelay: 0.03, wet: 1 });
  room.connect(master);
  await room.ready;
  const echo = new Tone.FeedbackDelay({
    delayTime: "8n.",
    feedback: 0.35,
    wet: 1,
  });
  echo.fan(master, new Tone.Gain(0.4).connect(room));

  // A channel strip: filter → dry level + reverb send + echo send.
  const channel = (filterType = "lowpass") => {
    const filter = new Tone.Filter({
      type: filterType,
      frequency: 20000,
      rolloff: -24,
    });
    const dry = new Tone.Gain(1).connect(master);
    const toRoom = new Tone.Gain(0).connect(room);
    const toEcho = new Tone.Gain(0).connect(echo);
    filter.fan(dry, toRoom, toEcho);
    return { input: filter, filter, dry, toRoom, toEcho };
  };
  const strips = {
    pad: channel(),
    bell: channel(),
    bass: channel(),
    drums: channel(),
  };

  // Pad: detuned saws with slow chorus, filtered dark, swelling in and out.
  const padChorus = new Tone.Chorus({
    frequency: 0.6,
    delayTime: 3.5,
    depth: 0.4,
    wet: 0.5,
  })
    .connect(strips.pad.input)
    .start();
  const pad = new Tone.PolySynth(Tone.Synth, {
    oscillator: { type: "fatsawtooth", count: 3, spread: 22 },
    envelope: { attack: 1.2, decay: 0.5, sustain: 0.8, release: 3 },
    volume: -20,
  }).connect(padChorus);

  // Bell: FM synthesis with an inharmonic ratio for a glassy ring.
  const bell = new Tone.PolySynth(Tone.FMSynth, {
    harmonicity: 3.01,
    modulationIndex: 12,
    oscillator: { type: "sine" },
    modulation: { type: "sine" },
    envelope: { attack: 0.005, decay: 1.6, sustain: 0, release: 1.6 },
    modulationEnvelope: { attack: 0.002, decay: 0.4, sustain: 0, release: 0.4 },
    volume: -14,
  }).connect(strips.bell.input);

  // Bass: a filtered saw with a little pluck at the start.
  const bass = new Tone.MonoSynth({
    oscillator: { type: "sawtooth" },
    filter: { type: "lowpass", Q: 1, rolloff: -24 },
    envelope: { attack: 0.02, decay: 0.3, sustain: 0.7, release: 0.6 },
    filterEnvelope: {
      attack: 0.01,
      decay: 0.3,
      sustain: 0.4,
      release: 0.6,
      baseFrequency: 110,
      octaves: 2.5,
    },
    volume: -12,
  }).connect(strips.bass.input);

  // Drums: soft on purpose — a round kick, a brushed snare, a quiet hat.
  const kick = new Tone.MembraneSynth({
    pitchDecay: 0.05,
    octaves: 6,
    envelope: { attack: 0.001, decay: 0.4, sustain: 0, release: 0.1 },
    volume: -8,
  }).connect(strips.drums.input);
  const snare = new Tone.NoiseSynth({
    noise: { type: "pink" },
    envelope: { attack: 0.001, decay: 0.18, sustain: 0 },
    volume: -14,
  }).connect(
    new Tone.Filter({ type: "bandpass", frequency: 1800, Q: 0.8 }).connect(
      strips.drums.input
    )
  );
  const hat = new Tone.NoiseSynth({
    noise: { type: "white" },
    envelope: { attack: 0.001, decay: 0.05, sustain: 0 },
    volume: -24,
  }).connect(
    new Tone.Filter({ type: "highpass", frequency: 7000 }).connect(
      strips.drums.input
    )
  );

  // Every instrument plays a MIDI note for a duration; drums read the note
  // number as which drum to hit.
  const instruments = {
    pad: {
      play: (m, d, t, v) => pad.triggerAttackRelease(midiToFreq(m), d, t, v),
      stop: () => pad.releaseAll(),
    },
    bell: {
      play: (m, d, t, v) => bell.triggerAttackRelease(midiToFreq(m), d, t, v),
      stop: () => bell.releaseAll(),
    },
    bass: {
      play: (m, d, t, v) => bass.triggerAttackRelease(midiToFreq(m), d, t, v),
      stop: () => bass.triggerRelease(),
    },
    drums: {
      play: (m, d, t, v) => {
        if (m === KICK) kick.triggerAttackRelease("C1", "8n", t, v);
        else if (m === SNARE) snare.triggerAttackRelease("16n", t, v);
        else if (m === HAT) hat.triggerAttackRelease("32n", t, v);
      },
      stop: () => {},
    },
  };

  let part = null;
  let ticksPerStep = 96;
  let loopTicks = 96 * 32;
  let sweep = null; // { from, to, seconds } while the pad's sweep is on

  // Apply each track's sound settings to its channel strip.
  const applySound = (song) => {
    Object.keys(strips).forEach((inst) => {
      const track = song.tracks.find((t) => t.instrument === inst);
      const sound = soundOf(track || { instrument: inst });
      const strip = strips[inst];
      strip.dry.gain.rampTo(1 - 0.5 * sound.reverb, 0.05);
      strip.toRoom.gain.rampTo(sound.reverb, 0.05);
      strip.toEcho.gain.rampTo(sound.delay, 0.05);
      const hz = brightnessHz(sound.brightness) * FILTER_SCALE[inst];
      if (inst === "drums") {
        // no brightness for drums
      } else if (inst === "bass") {
        bass.set({ filterEnvelope: { baseFrequency: 40 + hz * 0.2 } });
      } else if (!(inst === "pad" && sound.sweep)) {
        strip.filter.frequency.cancelScheduledValues(0);
        strip.filter.frequency.rampTo(Math.min(hz, 18000), 0.05);
      }
      if (inst === "pad") {
        pad.set({ envelope: { attack: sound.attack, release: sound.release } });
        sweep = sound.sweep
          ? {
              from: Math.max(120, hz * 0.3),
              to: brightnessHz(Math.min(1, sound.brightness + 0.35)),
              seconds: loopSeconds(song),
            }
          : null;
      }
    });
  };

  const setSong = (song) => {
    // A bar is four quarter notes; PPQ is transport ticks per quarter note.
    ticksPerStep = (transport.PPQ * 4) / song.stepsPerBar;
    loopTicks = ticksPerStep * totalSteps(song);
    transport.bpm.value = song.bpm;
    transport.loop = true;
    transport.loopStart = 0;
    transport.loopEnd = `${loopTicks}i`;
    applySound(song);

    if (part) part.dispose();
    const events = playbackEvents(song).map((e) => ({
      time: `${e.step * ticksPerStep}i`,
      duration: `${e.length * ticksPerStep}i`,
      instrument: e.instrument,
      pitch: e.pitch,
    }));
    // The pad's sweep restarts at the top of every pass of the loop.
    events.push({ time: 0, sweep: true });
    part = new Tone.Part((time, ev) => {
      if (ev.sweep) {
        if (!sweep) return;
        const f = strips.pad.filter.frequency;
        f.cancelScheduledValues(time);
        f.setValueAtTime(sweep.from, time);
        f.exponentialRampToValueAtTime(sweep.to, time + sweep.seconds * 0.95);
        return;
      }
      const inst = instruments[ev.instrument];
      if (inst) inst.play(ev.pitch, ev.duration, time, 0.8);
    }, events).start(0);
  };

  const play = () => {
    if (transport.state !== "started") transport.start("+0.05");
  };

  const stop = () => {
    transport.stop();
    Object.values(instruments).forEach((inst) => inst.stop());
  };

  const isPlaying = () => transport.state === "started";

  // Where the playhead is, in steps (fractional), or null when stopped.
  const position = () =>
    isPlaying() ? (transport.ticks % loopTicks) / ticksPerStep : null;

  // Sound one note right away, e.g. when it's placed on the piano roll.
  const preview = (instrument, midi) => {
    const inst = instruments[instrument];
    if (!inst) return;
    const length = instrument === "pad" ? 1.2 : 0.6;
    inst.play(midi, length, Tone.now() + 0.01, 0.7);
  };

  return { setSong, play, stop, isPlaying, position, preview };
};
